import { useCallback, useEffect, useMemo, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import TableHeadTypography from 'src/components/TableHeadTypography';
import useTypographyMediaQuery from 'src/hooks/useTypographyMediaQuery';
import Iconify from 'src/components/Iconify';
import ListActionBar from 'src/components/ListActionBar';
import ColumnVisibilityMenu from 'src/components/ColumnVisibilityMenu';
import TableContainer from 'src/components/Table/TableContainer';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import { formatDate } from 'src/utils/formatTime';
import { useTagOptions } from 'src/hooks/tag';
import { abbreviateHashes } from 'src/utils/helpers';
import HighlightedText from 'src/components/HighlightedText';
import { TableCellTypography } from 'src/components/GenericTableCell';
import { EXPANDED_ROWS_LIMIT } from 'src/constants/table';
import {
  useInfiniteUnmatchedFiles,
  useUpdateUnmatchedFileStatuses,
  useUpdateUnmatchedFileStatusesForFilter,
} from 'src/hooks/wdac';
import {
  UNMATCHED_FILE_STATUS,
  getUnmatchedFileStatusLabel,
} from 'src/utils/enums/unmatchedFileStatus';
import StatusButton from 'src/components/StatusButton';
import Image from 'src/components/Image';
import UnmatchedFileStatusModal from './UnmatchedFileStatusModal';
import UnmatchedFileDetailsModal from './UnmatchedFileDetailsModal';
import CatalogTrustWarning from './CatalogTrustWarning';
import { buildSharedParams } from './sharedQueryHelper';
import {
  DEFAULT_UNMATCHED_FILES_VISIBLE_COLUMNS,
  UNMATCHED_FILES_COLUMNS,
  UNMATCHED_FILE_TABLE_LAST_SEEN_RANGE_OPTIONS,
  UNMATCHED_FILE_TRUST_SIGNAL_OPTIONS,
} from './constants';
import { useColumnVisibility } from 'src/hooks/columnVisibility';

const CLOSED_STATUS_MODAL = { open: false, mode: 'selection', rows: [] };

const optionLabel = (options, value) =>
  options.find(option => option.value === value)?.label ?? value;

const UnmatchedColumnHeader = ({ label, tooltip, sortable, sortActive, sortDirection, onSort }) => {
  const { mediumScreen, largeScreen, smallScreen } = useTypographyMediaQuery();

  const headerLabel = (
    <TableHeadTypography
      hasSorting={sortActive}
      mediumScreen={mediumScreen}
      largeScreen={largeScreen}
      smallScreen={smallScreen}>
      {label}
    </TableHeadTypography>
  );

  const sortIcon = sortable ? (
    <Iconify
      icon={
        sortActive
          ? sortDirection === 'asc'
            ? 'mdi:arrow-up'
            : 'mdi:arrow-down'
          : 'mdi:swap-vertical'
      }
      width={14}
      height={14}
      color={sortActive ? '#7DFF92' : '#9CA3AF'}
    />
  ) : null;

  const row = (
    <Stack
      direction="row"
      gap={1}
      alignItems="center"
      onClick={sortable ? onSort : undefined}
      sx={{ cursor: sortable ? 'pointer' : tooltip ? 'help' : 'default' }}>
      {headerLabel}
      {sortIcon}
    </Stack>
  );

  return tooltip ? (
    <Tooltip
      title={tooltip}
      arrow
      componentsProps={{
        tooltip: {
          sx: { bgcolor: '#3F3F3F', maxWidth: 320 },
        },
      }}>
      <Box component="span" display="inline-flex">
        {row}
      </Box>
    </Tooltip>
  ) : (
    row
  );
};

const UnmatchedFilesTable = ({
  filters,
  onFiltersChange,
  search = '',
  onSearchChange,
  queriesEnabled = true,
  groupFilter = null,
  onClearGroupFilter,
  onViewFilesForGroup,
}) => {
  const { data: tagOptionsData } = useTagOptions({ search: '', enabled: true });
  const tagOptions = useMemo(() => tagOptionsData?.tags || [], [tagOptionsData]);
  const { enqueueSnackbar } = useSnackbar();

  const [sort, setSort] = useState({ column: 'lastSeen', direction: 'desc' });

  const trimmedSearch = search.trim();

  const apiQuery = useMemo(() => {
    const params = buildSharedParams(filters);
    if (trimmedSearch) {
      params.search = trimmedSearch;
    }
    if (sort.column) {
      params.sortColumn = sort.column;
      params.sortDesc = sort.direction === 'desc';
    }
    if (groupFilter?.params) {
      Object.assign(params, groupFilter.params);
    }
    return params;
  }, [filters, trimmedSearch, sort, groupFilter]);

  const unmatchedFilesQuery = useInfiniteUnmatchedFiles({
    apiQuery,
    limit: EXPANDED_ROWS_LIMIT,
    enabled: queriesEnabled,
  });

  const results = useMemo(
    () => unmatchedFilesQuery.data?.pages?.flatMap(page => page.results || []) || [],
    [unmatchedFilesQuery.data],
  );
  const totalCount = unmatchedFilesQuery.data?.pages?.[0]?.totalCount || 0;
  const isLoading = !queriesEnabled || unmatchedFilesQuery.isLoading;
  const isFetchingNextPage = unmatchedFilesQuery.isFetchingNextPage;

  const loadMore = useCallback(() => {
    if (unmatchedFilesQuery.hasNextPage) {
      unmatchedFilesQuery.fetchNextPage();
    }
  }, [unmatchedFilesQuery]);

  const [sentryRef] = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasNextPage: unmatchedFilesQuery.hasNextPage,
    disabled: !queriesEnabled,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px',
  });

  const onSortChange = setSort;

  const {
    visibleColumnDefinitions: visibleColumns,
    isColumnVisible,
    setColumnVisible,
    resetColumns,
  } = useColumnVisibility(
    'unmatchedFiles',
    UNMATCHED_FILES_COLUMNS,
    DEFAULT_UNMATCHED_FILES_VISIBLE_COLUMNS,
  );

  const columnWidths = useMemo(() => {
    if (visibleColumns.length === 0) {
      return {};
    }
    const weights = {
      fileName: 2,
      launchingProcess: 2,
      directory: 2.2,
      lastSeen: 1.2,
      devices: 0.9,
      noteColumn: 0.5,
    };
    const getWeight = key => weights[key] ?? 1;
    const totalParts = visibleColumns.reduce((sum, c) => sum + getWeight(c.key), 0);
    return Object.fromEntries(
      visibleColumns.map(c => [c.key, `${(getWeight(c.key) / totalParts) * 100}%`]),
    );
  }, [visibleColumns]);

  const filterOptions = useMemo(
    () => [
      {
        key: 'trustSignal',
        type: 'select',
        label: 'Signal',
        options: UNMATCHED_FILE_TRUST_SIGNAL_OPTIONS,
        disabled: currentFilters => currentFilters.trustStatus !== UNMATCHED_FILE_STATUS.Unknown,
      },
      {
        key: 'tagId',
        type: 'select',
        label: 'Device Tag',
        options: [
          { value: '', label: 'All Tags' },
          ...tagOptions.map(tag => ({ value: String(tag.id), label: tag.name })),
        ],
      },
      {
        key: 'lastSeenRange',
        type: 'select',
        label: 'Last Seen',
        options: UNMATCHED_FILE_TABLE_LAST_SEEN_RANGE_OPTIONS,
      },
      {
        key: 'trustStatus',
        type: 'select',
        label: 'Trust Status',
        options: [
          { value: '', label: 'All statuses' },
          ...Object.values(UNMATCHED_FILE_STATUS).map(value => ({
            value,
            label: getUnmatchedFileStatusLabel(value),
          })),
        ],
      },
    ],
    [tagOptions],
  );

  const onFilterChange = useCallback(
    nextFilters => {
      const tagValue =
        nextFilters.tagId === null || nextFilters.tagId === undefined ? '' : nextFilters.tagId;
      onFiltersChange({
        ...filters,
        tagId: tagValue,
        trustStatus: nextFilters.trustStatus || '',
        trustSignal:
          nextFilters.trustStatus === UNMATCHED_FILE_STATUS.Unknown
            ? nextFilters.trustSignal || ''
            : '',
        lastSeenRange: nextFilters.lastSeenRange || '',
      });
    },
    [filters, onFiltersChange],
  );

  const [selectedRows, setSelectedRows] = useState(() => new Set());

  useEffect(() => {
    setSelectedRows(new Set());
  }, [filters, trimmedSearch, groupFilter]);

  const toggleRowSelection = useCallback(row => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(row.id)) {
        next.delete(row.id);
      } else {
        next.add(row.id);
      }
      return next;
    });
  }, []);

  const handleSort = useCallback(
    column => {
      if (!column.sortable || !column.sortKey) {
        return;
      }

      if (sort.column === column.sortKey) {
        onSortChange({
          column: column.sortKey,
          direction: sort.direction === 'asc' ? 'desc' : 'asc',
        });
      } else {
        onSortChange({ column: column.sortKey, direction: 'desc' });
      }
    },
    [onSortChange, sort],
  );

  const [statusModal, setStatusModal] = useState(CLOSED_STATUS_MODAL);
  const [detailsModal, setDetailsModal] = useState({ open: false, row: null });

  const updateStatusesMutation = useUpdateUnmatchedFileStatuses();
  const updateFilterStatusesMutation = useUpdateUnmatchedFileStatusesForFilter();

  // The filter endpoint scopes by the same predicates as the list, minus presentation-only sorting.
  const filterScopeQuery = useMemo(() => {
    const { sortColumn, sortDesc, ...scope } = apiQuery;
    return scope;
  }, [apiQuery]);

  const openEditModal = useCallback(row => {
    setStatusModal({ open: true, mode: 'selection', rows: [row] });
  }, []);

  const openBulkModal = useCallback(() => {
    const rows = (results || []).filter(r => selectedRows.has(r.id));
    if (rows.length === 0) {
      return;
    }
    setStatusModal({ open: true, mode: 'selection', rows });
  }, [results, selectedRows]);

  const openFilterModal = useCallback(() => {
    setStatusModal({ open: true, mode: 'filter', rows: [] });
  }, []);

  // Mode is kept on close so the dialog body does not swap during the closing transition.
  const closeStatusModal = useCallback(
    () => setStatusModal(prev => ({ ...prev, open: false })),
    [],
  );

  const openDetailsModal = useCallback(row => {
    setDetailsModal({ open: true, row });
  }, []);

  const isFilterMode = statusModal.mode === 'filter';

  const saveStatusModal = useCallback(
    async (newStatus, note) => {
      const ids = statusModal.rows.map(r => r.id).filter(Boolean);
      if (!isFilterMode && ids.length === 0) {
        return;
      }

      try {
        let count;
        if (isFilterMode) {
          count =
            (await updateFilterStatusesMutation.mutateAsync({
              query: filterScopeQuery,
              payload: { status: newStatus, notes: note },
            })) ?? 0;
        } else {
          await updateStatusesMutation.mutateAsync({
            customerEnvironmentUnmatchedFileStatusIds: ids,
            status: newStatus,
            notes: note,
          });
          count = ids.length;
        }

        enqueueSnackbar(
          count === 1 ? 'Status updated' : `${count.toLocaleString()} statuses updated`,
          { variant: 'success', autoHideDuration: 3000 },
        );
        closeStatusModal();
        if (count > 1) {
          setSelectedRows(new Set());
        }
      } catch (error) {
        enqueueSnackbar(`Could not update status: ${error?.message || error}`, {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    },
    [
      closeStatusModal,
      enqueueSnackbar,
      filterScopeQuery,
      isFilterMode,
      statusModal.rows,
      updateFilterStatusesMutation,
      updateStatusesMutation,
    ],
  );

  // Spelled out with the same labels the filter bar and table use, so the dialog is auditable
  // before committing a write that reaches rows not on screen.
  const filterScope = useMemo(() => {
    if (!isFilterMode) {
      return undefined;
    }

    const items = [];
    if (groupFilter) {
      items.push({
        label: `${groupFilter.groupLabel} Group`,
        value: abbreviateHashes(groupFilter.groupValue),
      });
    }
    if (trimmedSearch) {
      items.push({ label: 'Search', value: abbreviateHashes(trimmedSearch) });
    }
    if (filters.trustStatus) {
      items.push({
        label: 'Trust Status',
        value: getUnmatchedFileStatusLabel(filters.trustStatus),
      });
    }
    if (filters.trustSignal) {
      items.push({
        label: 'Signal',
        value: optionLabel(UNMATCHED_FILE_TRUST_SIGNAL_OPTIONS, filters.trustSignal),
      });
    }
    if (filters.tagId) {
      items.push({
        label: 'Device Tag',
        value:
          tagOptions.find(tag => String(tag.id) === String(filters.tagId))?.name || filters.tagId,
      });
    }
    if (filters.lastSeenRange) {
      items.push({
        label: 'Last Seen',
        value: optionLabel(
          UNMATCHED_FILE_TABLE_LAST_SEEN_RANGE_OPTIONS,
          filters.lastSeenRange,
        ),
      });
    }

    if (items.length === 0) {
      items.push({
        label: 'Scope',
        value: 'No filters applied — every unmatched file in this environment',
      });
    }

    return {
      description: `This will change all ${totalCount.toLocaleString()} file${
        totalCount === 1 ? '' : 's'
      } matching the current filter, including any not loaded on screen.`,
      items,
    };
  }, [filters, groupFilter, isFilterMode, tagOptions, totalCount, trimmedSearch]);

  const renderCell = useCallback(
    (column, row) => {
      switch (column.key) {
        case 'status':
          return (
            <TableCell key={`${row.id}-status`} sx={{ py: 0.5, px: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box
                  className="hover-checkbox"
                  sx={{
                    opacity: selectedRows.has(row.id) ? 1 : 0.35,
                    transition: 'opacity 0.2s',
                    '&:hover': {
                      opacity: 1,
                      '& .MuiSvgIcon-root path': { fill: '#7DFF92' },
                    },
                  }}>
                  <Checkbox
                    size="small"
                    checked={selectedRows.has(row.id)}
                    onChange={() => toggleRowSelection(row)}
                    onClick={e => e.stopPropagation()}
                    sx={{ '& .MuiSvgIcon-root path': { fill: '#7DFF92' } }}
                  />
                </Box>
                <StatusButton
                  status={row.trustStatus}
                  size="small"
                  sx={{ fontSize: '11px', padding: '2px 6px', minWidth: 0 }}
                  onClick={e => {
                    e.stopPropagation();
                    openEditModal(row);
                  }}>
                  {getUnmatchedFileStatusLabel(row.trustStatus)}
                </StatusButton>
                <CatalogTrustWarning
                  trustStatus={row.trustStatus}
                  trustedAsOfVersion={row.trustedAsOfVersion}
                />
              </Stack>
            </TableCell>
          );
        case 'fileName':
          return (
            <TableCell
              key={`${row.id}-file`}
              sx={{
                py: 0.5,
                verticalAlign: 'middle',
                minWidth: 0,
              }}>
              <Stack
                spacing={0}
                gap="2px"
                justifyContent="center"
                sx={{ minWidth: 0, maxWidth: '100%' }}>
                <Box sx={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
                  <TableCellTypography
                    sx={{
                      fontSize: '0.7rem',
                      lineHeight: 1.25,
                      m: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                      maxWidth: '100%',
                      display: 'block',
                    }}>
                    <HighlightedText text={row.fileName} searchValue={trimmedSearch} fallback="—" />
                  </TableCellTypography>
                </Box>
                <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
                  <TableCellTypography
                    component="div"
                    sx={{
                      fontSize: '0.65rem',
                      color: '#9CA3AF',
                      lineHeight: 1.25,
                      m: 0,
                      minWidth: 0,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                    <HighlightedText text={row.sha256} searchValue={trimmedSearch} fallback="—" />
                  </TableCellTypography>
                </Box>
              </Stack>
            </TableCell>
          );
        case 'matchedApps':
          return (
            <TableCell key={`${row.id}-matched`} align="center" sx={{ py: 0.5 }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                {row.numTrustedGlobalApps ?? 0}
              </Typography>
            </TableCell>
          );
        case 'signers':
          return (
            <TableCell key={`${row.id}-signers`} align="center" sx={{ py: 0.5 }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                {row.numSigners ?? 0}
              </Typography>
            </TableCell>
          );
        case 'trustedSigners':
          return (
            <TableCell key={`${row.id}-trustedSigners`} align="center" sx={{ py: 0.5 }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                {row.numTrustedSigners ?? 0}
              </Typography>
            </TableCell>
          );
        case 'trustedPublisherNames':
          return (
            <TableCell key={`${row.id}-trustedPublisherNames`} align="center" sx={{ py: 0.5 }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                {row.numTrustedIssuersWithPublisherName ?? 0}
              </Typography>
            </TableCell>
          );
        case 'trustedIssuers':
          return (
            <TableCell key={`${row.id}-trustedIssuers`} align="center" sx={{ py: 0.5 }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                {row.numTrustedIssuers ?? 0}
              </Typography>
            </TableCell>
          );
        case 'lastSeen':
          return (
            <TableCell key={`${row.id}-lastSeen`} sx={{ py: 0.5 }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                {row.lastSeenDate ? formatDate(row.lastSeenDate, 'yyyy-MM-dd') : '—'}
              </Typography>
            </TableCell>
          );
        case 'devices':
          return (
            <TableCell key={`${row.id}-devices`} sx={{ py: 0.5 }}>
              <Stack spacing={0.15}>
                <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                  Last 30: <strong>{row.numDevicesWithEventsWithin30OfLastSeen ?? 0}</strong>
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                  Last 90: <strong>{row.numDevicesWithEventsWithin90OfLastSeen ?? 0}</strong>
                </Typography>
              </Stack>
            </TableCell>
          );
        case 'launchingProcess': {
          const launchingProcess = row.mostCommonLaunchingProcess || '—';
          return (
            <TableCell key={`${row.id}-launch`} sx={{ py: 0.5, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.6rem',
                  lineHeight: 1.25,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-all',
                }}>
                {launchingProcess}
              </Typography>
            </TableCell>
          );
        }
        case 'directory': {
          const directory = row.mostCommonFileDirectory || '—';
          return (
            <TableCell key={`${row.id}-path`} sx={{ py: 0.5, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.6rem',
                  lineHeight: 1.25,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-all',
                }}>
                {directory}
              </Typography>
            </TableCell>
          );
        }
        case 'noteColumn':
          return (
            <TableCell
              key={`${row.id}-noteCol`}
              align="center"
              sx={{ py: 0.25, px: 0.25, width: 40, maxWidth: 44, minWidth: 36 }}>
              {row.note ? (
                <Tooltip
                  title={row.note}
                  arrow
                  componentsProps={{ tooltip: { sx: { bgcolor: '#3F3F3F' } } }}>
                  <Box
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    width={22}
                    height={22}>
                    <Image
                      src="/static/icons/notes-notepad.svg"
                      alt="Note"
                      width={18}
                      height={18}
                    />
                  </Box>
                </Tooltip>
              ) : (
                <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.7rem' }}>
                  —
                </Typography>
              )}
            </TableCell>
          );
        default:
          return <TableCell key={`${row.id}-${column.key}`} />;
      }
    },
    [selectedRows, toggleRowSelection, openEditModal, trimmedSearch],
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <ListActionBar
        onSearch={onSearchChange}
        defaultSearch={search}
        searchPlaceholder="File name or SHA-256 hash"
        filters={filters}
        onFilterChange={onFilterChange}
        filterOptions={filterOptions}
        filterChips
        limitChips={3}
        leadingChips={
          groupFilter ? (
            <Chip
              className="filter-chip"
              label={groupFilter.label}
              onDelete={onClearGroupFilter}
              sx={{
                maxWidth: 240,
                backgroundColor: 'rgba(135, 200, 224, 0.28)',
                color: '#EAF6FB',
              }}
            />
          ) : null
        }
        filterButtonCount={totalCount}
        actionArea={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <ColumnVisibilityMenu
              columns={UNMATCHED_FILES_COLUMNS}
              isVisible={isColumnVisible}
              getLabel={column => column.label || column.key}
              onChange={setColumnVisible}
              onReset={resetColumns}
            />
            <Button
              variant="outlined"
              onClick={selectedRows.size > 0 ? openBulkModal : openFilterModal}
              sx={{
                backgroundColor: '#3F3F3F',
                borderColor: '#2E3236',
                color: '#ffffff',
                textTransform: 'none',
                padding: '4px 14px',
                minHeight: 32,
              }}>
              {selectedRows.size > 0
                ? `Change Status (${selectedRows.size} file${selectedRows.size === 1 ? '' : 's'})`
                : 'Change All Statuses'}
            </Button>
          </Stack>
        }
      />

      <TableContainer
        roundedTop
        styles={{
          maxHeight: 480,
        }}>
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': { fontSize: '0.7rem' },
          }}>
          <colgroup>
            {visibleColumns.map(column => (
              <col key={column.key} style={{ width: columnWidths[column.key] }} />
            ))}
          </colgroup>
          <TableHead sx={{ backgroundColor: '#3f3f3f', borderRadius: 0 }}>
            <TableRow>
              {visibleColumns.map(column => {
                const isSortable = !!column.sortable;
                const sortActive = isSortable && sort.column === column.sortKey;
                if (column.key === 'status') {
                  return (
                    <TableCell
                      key={column.key}
                      sx={{
                        py: '8px',
                        px: 1,
                        verticalAlign: 'middle',
                      }}>
                      <UnmatchedColumnHeader
                        label={column.label}
                        tooltip={column.tooltip}
                        sortable={!!column.sortable}
                        sortActive={!!column.sortKey && sort.column === column.sortKey}
                        sortDirection={sort.direction}
                        onSort={() => handleSort(column)}
                      />
                    </TableCell>
                  );
                }
                return (
                  <TableCell
                    key={column.key}
                    sx={{
                      p: '8px 16px',
                      verticalAlign: 'middle',
                    }}>
                    <UnmatchedColumnHeader
                      label={column.label}
                      tooltip={column.tooltip}
                      sortable={isSortable}
                      sortActive={sortActive}
                      sortDirection={sort.direction}
                      onSort={() => handleSort(column)}
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
            {isLoading ? (
              <LoadingRows columnCount={visibleColumns.length} rowCount={10} />
            ) : (
              <>
                {results?.map((row, index) => (
                  <TableRow
                    key={row.id || row.sha256 || index}
                    ref={index === results.length - 1 ? sentryRef : null}
                    onClick={() => openDetailsModal(row)}
                    sx={{
                      '& td': { py: 0.35 },
                      cursor: 'pointer',
                    }}>
                    {visibleColumns.map(column => renderCell(column, row))}
                  </TableRow>
                ))}
                {!isLoading && isFetchingNextPage && (
                  <LoadingRows columnCount={visibleColumns.length} rowCount={3} />
                )}
                {!isLoading && !isFetchingNextPage && !results?.length && (
                  <NoDataCell colSpan={visibleColumns.length}>No unmatched files found.</NoDataCell>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <UnmatchedFileStatusModal
        open={statusModal.open}
        onClose={closeStatusModal}
        rows={statusModal.rows}
        count={isFilterMode ? totalCount : undefined}
        // The info alert already states the scope, so the summary line is suppressed for it.
        summary={isFilterMode ? '' : undefined}
        filterScope={filterScope}
        onSave={saveStatusModal}
        loading={updateStatusesMutation.isLoading || updateFilterStatusesMutation.isLoading}
      />

      <UnmatchedFileDetailsModal
        open={detailsModal.open}
        onClose={() => setDetailsModal({ open: false, row: null })}
        statusId={detailsModal.row?.id}
        fallbackRow={detailsModal.row}
        onViewFilesForGroup={onViewFilesForGroup}
      />
    </Box>
  );
};

export default UnmatchedFilesTable;
